import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { BR, CN, DE, ES, FR, GB, IT, JP, KR, RU } from 'country-flag-icons/react/3x2'
import { useTranslation } from 'react-i18next'
import { trackPageView } from './analytics'
import i18n, { currentLocale, localeMeta, locales, localizedPath, type Locale } from './i18n'

const localeFlags: Record<Locale, typeof GB> = {
  en: GB,
  es: ES,
  pt: BR,
  de: DE,
  fr: FR,
  it: IT,
  ru: RU,
  zh: CN,
  ja: JP,
  ko: KR,
}

function LocaleFlag({ locale }: { locale: Locale }) {
  const Flag = localeFlags[locale]
  return (
    <span className="locale-flag" aria-hidden="true">
      <Flag focusable="false" />
    </span>
  )
}

export default function LanguageSwitcher({ reloadOnChange = false }: { reloadOnChange?: boolean }) {
  const { t } = useTranslation()
  const locale = currentLocale()
  const menuId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(() => locales.indexOf(locale))

  async function changeLocale(nextLocale: Locale) {
    if (nextLocale === locale) return
    const nextPath = localizedPath(nextLocale)
    if (reloadOnChange) {
      window.location.assign(nextPath)
      return
    }
    window.history.pushState({ locale: nextLocale }, '', nextPath)
    await i18n.changeLanguage(nextLocale)
    trackPageView()
  }

  useEffect(() => {
    if (!open) return
    itemRefs.current[activeIndex]?.focus()

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', closeOnOutsidePointer)
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer)
  }, [activeIndex, open])

  function openMenu(index = locales.indexOf(locale)) {
    setActiveIndex(index)
    setOpen(true)
  }

  function closeMenu({ restoreFocus = false } = {}) {
    setOpen(false)
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus())
  }

  function selectLocale(nextLocale: Locale) {
    closeMenu({ restoreFocus: true })
    void changeLocale(nextLocale)
  }

  function handleTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openMenu()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      openMenu(locales.length - 1)
    } else if (event.key === 'Escape') {
      closeMenu()
    }
  }

  function handleMenuKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((activeIndex + step + locales.length) % locales.length)
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      setActiveIndex(event.key === 'Home' ? 0 : locales.length - 1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      selectLocale(locales[activeIndex])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu({ restoreFocus: true })
    } else if (event.key === 'Tab') {
      closeMenu()
    }
  }

  return (
    <div className="language-picker" ref={rootRef}>
      <button
        className="language-switcher"
        type="button"
        ref={triggerRef}
        aria-label={t('accessibility.language')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => open ? closeMenu() : openMenu()}
        onKeyDown={handleTriggerKeyDown}
      >
        <LocaleFlag locale={locale} />
        <span className="language-switcher__code" aria-hidden="true">{localeMeta[locale].shortLabel}</span>
      </button>

      {open && (
        <div className="language-menu" id={menuId} role="listbox" aria-label={t('accessibility.language')}>
          <div className="language-menu__head">
            <span>{t('accessibility.language')}</span>
            <small>{locales.length.toString().padStart(2, '0')}</small>
          </div>
          <div className="language-menu__viewport">
            {locales.map((item) => (
              <button
                className={`language-menu__item${locales[activeIndex] === item ? ' is-active' : ''}`}
                type="button"
                role="option"
                aria-selected={locale === item}
                tabIndex={locales[activeIndex] === item ? 0 : -1}
                ref={(element) => { itemRefs.current[locales.indexOf(item)] = element }}
                onFocus={() => setActiveIndex(locales.indexOf(item))}
                onPointerMove={() => setActiveIndex(locales.indexOf(item))}
                onClick={() => selectLocale(item)}
                onKeyDown={handleMenuKeyDown}
                key={item}
              >
                <LocaleFlag locale={item} />
                <span className="language-menu__label">{localeMeta[item].label}</span>
                <span className="language-menu__code" aria-hidden="true">{localeMeta[item].shortLabel}</span>
                <span className="language-menu__indicator" aria-hidden="true">{locale === item ? '✓' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
